// src/business/components/BusinessTopbar.jsx
import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, LogOut, Moon, Sun } from "lucide-react";

import { useTheme } from "../../context/ThemeContext";
import { useBusinessAuth } from "../context/BusinessAuthContext";
import { markBusinessLogoutInProgress } from "./RequireBusiness";
import BusinessNotificationBell from "./BusinessNotificationBell";
import { businessRiderNavigation } from "./BusinessSidebar";
import { businessBreadcrumb, navigationAccess } from "../navigation/businessNavigation";
import { Breadcrumb, ConfirmDialog, Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger, cn, focusRing } from "../../components/ui";

const LOGOUT_FLAG = "business_logout_in_progress";

function titleize(value = "") {
  return value
    .replace(/[-_]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * BusinessTopbar — where you are (Module › Page) on the left; notifications
 * and the account menu on the right. The page title itself belongs to the
 * page's h1, so the topbar does not repeat it.
 */
export default function BusinessTopbar() {
  const auth = useBusinessAuth();
  const { user, role, logout } = auth;
  const { riderOnly } = businessRiderNavigation(auth);
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();

  const crumbs = useMemo(
    () => businessBreadcrumb(location, navigationAccess(auth), { riderOnly }),
    // auth identity drives which module link is offered
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, location.search, riderOnly, auth.role, auth.permissions]
  );

  const nameParts = [user?.firstName, user?.lastName].map((part) => String(part || "").trim()).filter(Boolean);
  const displayName = nameParts.join(" ") || user?.email || "User";
  const initials = nameParts.length
    ? nameParts.map((part) => Array.from(part)[0]).join("").toUpperCase()
    : Array.from(displayName).slice(0, 2).join("").toUpperCase();
  const roleLabel = role ? titleize(String(role).toLowerCase().replace(/_/g, " ")) : "";

  // Logging out ends this session only; nothing is lost and signing in again undoes it. One plain confirmation
  // (DESIGN.md › Consequential actions) keeps a slip in the account menu from ending someone's work, and a refusal
  // stays in the dialog. ConfirmDialog runs one submission at a time.
  const [confirmingLogout, setConfirmingLogout] = useState(false);

  async function logOut() {
    // RequireBusiness must not redirect while the session is being ended.
    sessionStorage.setItem(LOGOUT_FLAG, "1");
    markBusinessLogoutInProgress?.();
    try {
      await logout();
    } catch (error) {
      sessionStorage.removeItem(LOGOUT_FLAG);
      throw error;
    }
    sessionStorage.removeItem(LOGOUT_FLAG);
    // A hard redirect keeps the back button and page cache from reopening the portal.
    window.location.replace("/business/login");
  }

  return (
    <div className="flex h-full items-center justify-between gap-3">
      <Breadcrumb items={crumbs} className="flex-1" />

      <div className="flex shrink-0 items-center gap-1">
        <BusinessNotificationBell />

        <Menu modal={false}>
          <MenuTrigger asChild>
            <button
              type="button"
              aria-label={`Account menu: ${[displayName, roleLabel].filter(Boolean).join(", ")}`}
              className={cn(
                "group flex h-9 items-center gap-2 rounded-control pl-1 pr-1.5 text-left transition-colors hover:bg-hover max-md:h-11",
                focusRing
              )}
            >
              <span aria-hidden="true" className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-meta font-semibold text-secondary">
                {initials}
              </span>
              <span aria-hidden="true" className="hidden min-w-0 lg:block">
                <span className="block max-w-40 truncate text-body-sm font-medium text-foreground">{displayName}</span>
              </span>
              <ChevronDown aria-hidden="true" className="hidden h-3.5 w-3.5 shrink-0 text-tertiary transition-transform group-data-[state=open]:rotate-180 sm:block" />
            </button>
          </MenuTrigger>
          <MenuContent className="w-64">
            <MenuLabel className="py-2">
              <span className="block truncate text-body-sm font-medium text-foreground">{displayName}</span>
              {roleLabel ? <span className="block text-meta text-tertiary">{roleLabel}</span> : null}
            </MenuLabel>
            <MenuSeparator />
            <MenuItem
              icon={isDark ? Sun : Moon}
              onSelect={(event) => {
                event.preventDefault();
                toggleTheme();
              }}
            >
              {isDark ? "Use light theme" : "Use dark theme"}
            </MenuItem>
            <MenuSeparator />
            <MenuItem tone="danger" icon={LogOut} onSelect={() => setConfirmingLogout(true)}>
              Log out
            </MenuItem>
          </MenuContent>
        </Menu>
      </div>

      {confirmingLogout ? (
        <ConfirmDialog
          title="Log out?"
          description="You will need to sign in again to use the Business Portal."
          confirmLabel="Log out"
          cancelLabel="Stay signed in"
          failureTitle="Not logged out"
          errorFallback="We couldn't sign you out. Check your connection and try again."
          onConfirm={logOut}
          onClose={() => setConfirmingLogout(false)}
        />
      ) : null}
    </div>
  );
}
